import { createNativeInstance } from '@transifex/native';

import { getConfig } from '@/modules/runtimeConfig';

/**
 * The onboarding flow's own Transifex Native instance.
 *
 * `@transifex/native` exposes a process-wide default `tx`, and `useT()` / `<T>` bind
 * to it unless a `TXProvider` scopes them elsewhere. That default is shared mutable
 * state: a host app calling `tx.init({ token: theirs })` replaces the token our
 * strings were fetched under, and every onboarding string silently falls back to its
 * English source. Nothing throws, nothing logs.
 *
 * `assessmentTx` already solved this for the dynamicAssessments module. This is the
 * same fix one tier up, for the app's own screens — `OnboardingRoot` scopes them to
 * this instance via `TXProvider`, so our translations and a host's cannot collide in
 * either direction: we never read their token and never touch it.
 *
 * Modelled on `@vidahealth/login`'s `src/i18n/loginTx.ts` (`../login`).
 *
 * Created with an empty token at import so the instance always exists;
 * `configureOnboardingTx` supplies the real one from runtime config. A missing token
 * is not an error — it renders source strings, the correct degradation for a host
 * that hasn't wired translations yet.
 *
 * Note this instance and `assessmentTx` are separate on purpose even though the app
 * currently points both at the same Transifex project. The assessment module ships
 * independently to VidaReactNative, which supplies that token itself and never mounts
 * `OnboardingRoot` — one instance could not serve both callers.
 */
export const onboardingTx = createNativeInstance({ token: '' });

/**
 * Point `onboardingTx` at the configured token, idempotently.
 *
 * Called by `OnboardingRoot` **during render**, immediately after `setRuntimeConfig` —
 * the same place and order `LoginRoot` calls `configureLoginTxToken()`. Render-time
 * rather than in an effect because `useT()` subscriptions in children are set up in
 * passive effects, which React flushes after the parent has rendered: initializing
 * here means the token is in place before any child subscribes.
 *
 * The equality check is what makes that safe to do during render. `init()` mutates a
 * module singleton, so re-running it on every render would discard the fetched
 * translation cache each time; guarded, a repeat call is a no-op. That also covers
 * StrictMode's double invoke and a second `OnboardingRoot` mounted elsewhere.
 *
 * Reads the token through `getConfig('transifexToken')` rather than taking it as an argument,
 * so config remains the single channel for env-derived values.
 */
export const configureOnboardingTx = (): void => {
  const next = getConfig('transifexToken') ?? '';
  if (onboardingTx.token === next) return;
  onboardingTx.init({ token: next });
};
